// Environment atmosphere — Time of Day + Weather.
// Consumed by every map. Handles sky colour, sun angle/colour, ambient level,
// fog density, and weather particle overlays (rain / snow).
import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Sky, Cloud, Clouds } from "@react-three/drei";

// -------------- Presets ------------------------------------------------------

// Sun elevation + intensity + tone per ToD
const TOD = {
    morning: { sun: [15, 12, -8],  sunColor: "#ffd6a8", sunI: 0.85, ambient: 0.42, ambientColor: "#d0e0ff", sky: { turbidity: 6, rayleigh: 2.5, mieCoefficient: 0.006, mieDirectionalG: 0.85, azimuth: 0.15, inclination: 0.15 } },
    day:     { sun: [20, 32,  10], sunColor: "#ffffff", sunI: 1.15, ambient: 0.55, ambientColor: "#a4c6ff", sky: { turbidity: 4, rayleigh: 1.2, mieCoefficient: 0.005, mieDirectionalG: 0.8,  azimuth: 0.25, inclination: 0.42 } },
    sunset:  { sun: [22, 6,  -18], sunColor: "#ff8a3a", sunI: 0.95, ambient: 0.32, ambientColor: "#ffbd88", sky: { turbidity: 10, rayleigh: 3.5, mieCoefficient: 0.009, mieDirectionalG: 0.9, azimuth: 0.6,  inclination: 0.06 } },
    evening: { sun: [-15, 3, -22], sunColor: "#4a5a8a", sunI: 0.35, ambient: 0.22, ambientColor: "#5a6a9a", sky: { turbidity: 14, rayleigh: 4,   mieCoefficient: 0.014, mieDirectionalG: 0.9, azimuth: 0.75, inclination: 0.02 } },
    night:   { sun: [0, 20, 0],    sunColor: "#3a4a70", sunI: 0.12, ambient: 0.16, ambientColor: "#2a3450", sky: { turbidity: 20, rayleigh: 0.02,mieCoefficient: 0.02,  mieDirectionalG: 0.5, azimuth: 0.9,  inclination: -0.02 } },
};

const WEATHER = {
    sunny:      { fogNear: 40, fogFar: 120, fogColor: "#c9d5e0", clouds: 0.10, rain: 0,     snow: 0,     ambientMult: 1.0,  sunMult: 1.0 },
    cloudy:     { fogNear: 25, fogFar: 90,  fogColor: "#8a929c", clouds: 0.60, rain: 0,     snow: 0,     ambientMult: 0.85, sunMult: 0.65 },
    rain:       { fogNear: 12, fogFar: 55,  fogColor: "#4d5560", clouds: 0.85, rain: 400,   snow: 0,     ambientMult: 0.7,  sunMult: 0.35 },
    heavy_rain: { fogNear: 8,  fogFar: 42,  fogColor: "#3a4048", clouds: 0.95, rain: 900,   snow: 0,     ambientMult: 0.55, sunMult: 0.2  },
    snow:       { fogNear: 12, fogFar: 60,  fogColor: "#c3ccd6", clouds: 0.80, rain: 0,     snow: 700,   ambientMult: 1.0,  sunMult: 0.65 },
    fog:        { fogNear: 3,  fogFar: 30,  fogColor: "#8b8e94", clouds: 0.90, rain: 0,     snow: 0,     ambientMult: 0.75, sunMult: 0.35 },
};

// Default so unknown values still render
const defaultTod = "day";
const defaultWeather = "sunny";

export function getEnvironmentPreset(timeOfDay, weather) {
    return {
        tod: TOD[timeOfDay] || TOD[defaultTod],
        weather: WEATHER[weather] || WEATHER[defaultWeather],
    };
}

// -------------- Environment component ---------------------------------------

/**
 * Renders global atmosphere (sky, sun, ambient, fog, weather).
 * Use inside a map component ABOVE the geometry. Shadows are opt-in.
 */
export default function EnvironmentAtmosphere({
    timeOfDay = "day",
    weather = "sunny",
    shadows = false,
    // Override sun position (some maps want it fixed for gameplay reasons)
    sunPositionOverride,
}) {
    const { tod, weather: w } = useMemo(
        () => getEnvironmentPreset(timeOfDay, weather),
        [timeOfDay, weather]
    );

    const sunPos = sunPositionOverride || tod.sun;
    const sunI = tod.sunI * w.sunMult;
    const ambI = tod.ambient * w.ambientMult;

    // Precompute sky vector for drei Sky (unit vector from spherical params)
    const skyVec = useMemo(() => {
        const t = tod.sky;
        const inc = Math.PI * (0.5 - t.inclination);
        const az = 2 * Math.PI * (t.azimuth - 0.5);
        return [Math.sin(inc) * Math.cos(az), Math.cos(inc), Math.sin(inc) * Math.sin(az)];
    }, [tod]);

    return (
        <>
            {/* Sky — night uses a plain dark backdrop instead of drei Sky */}
            {timeOfDay === "night" ? (
                <>
                    <color attach="background" args={["#04060c"]} />
                    <mesh position={[0, 0, 0]}>
                        <sphereGeometry args={[500, 32, 32]} />
                        <meshBasicMaterial color="#04060c" side={THREE.BackSide} fog={false} />
                    </mesh>
                    {/* Simple star field */}
                    <StarField />
                </>
            ) : (
                <Sky
                    distance={4000}
                    sunPosition={skyVec}
                    turbidity={tod.sky.turbidity}
                    rayleigh={tod.sky.rayleigh}
                    mieCoefficient={tod.sky.mieCoefficient}
                    mieDirectionalG={tod.sky.mieDirectionalG}
                />
            )}

            {/* Fog */}
            <fog attach="fog" args={[w.fogColor, w.fogNear, w.fogFar]} />

            {/* Lighting */}
            <ambientLight intensity={ambI} color={tod.ambientColor} />
            <hemisphereLight
                args={[tod.ambientColor, "#101418", ambI * 0.6]}
            />
            <directionalLight
                position={sunPos}
                intensity={sunI}
                color={tod.sunColor}
                castShadow={shadows}
                shadow-mapSize-width={1024}
                shadow-mapSize-height={1024}
                shadow-camera-left={-40}
                shadow-camera-right={40}
                shadow-camera-top={40}
                shadow-camera-bottom={-40}
                shadow-bias={-0.0005}
            />

            {/* Cloud volumetrics — cheap, faded when fog is dense */}
            {w.clouds > 0.15 && w.fogFar > 20 && (
                <Clouds material={THREE.MeshBasicMaterial} limit={20}>
                    <Cloud
                        seed={1}
                        segments={20}
                        bounds={[80, 6, 40]}
                        volume={22}
                        color={tod.ambientColor}
                        opacity={w.clouds * 0.55}
                        position={[0, 45, -20]}
                    />
                    <Cloud
                        seed={2}
                        segments={16}
                        bounds={[60, 5, 30]}
                        volume={16}
                        color={tod.ambientColor}
                        opacity={w.clouds * 0.45}
                        position={[-40, 55, 30]}
                    />
                </Clouds>
            )}

            {/* Weather particles */}
            {w.rain > 0 && <RainField count={w.rain} tint={tod.ambientColor} />}
            {w.snow > 0 && <SnowField count={w.snow} />}

            {/* Night lamps overlay — soft warm points for playable night */}
            {timeOfDay === "night" && (
                <>
                    <pointLight position={[-20, 8, -15]} intensity={0.7} color="#ffb26b" distance={30} decay={2} />
                    <pointLight position={[20, 8, 15]} intensity={0.7} color="#ffb26b" distance={30} decay={2} />
                    <pointLight position={[0, 12, 0]} intensity={0.3} color="#a4d9e6" distance={40} decay={2} />
                </>
            )}
        </>
    );
}

// -------------- Weather particle fields -------------------------------------

function RainField({ count = 300, tint = "#a4b6c8" }) {
    const meshRef = useRef();
    const dummy = useMemo(() => new THREE.Object3D(), []);
    const drops = useMemo(() => {
        const arr = [];
        for (let i = 0; i < count; i++) {
            arr.push({
                x: (Math.random() - 0.5) * 80,
                y: 5 + Math.random() * 25,
                z: (Math.random() - 0.5) * 80,
                s: 8 + Math.random() * 8, // fall speed
            });
        }
        return arr;
    }, [count]);

    useFrame((_, dt) => {
        if (!meshRef.current) return;
        for (let i = 0; i < drops.length; i++) {
            const d = drops[i];
            d.y -= d.s * dt;
            if (d.y < 0) d.y = 25 + Math.random() * 5;
            dummy.position.set(d.x, d.y, d.z);
            dummy.rotation.set(0, 0, 0);
            dummy.updateMatrix();
            meshRef.current.setMatrixAt(i, dummy.matrix);
        }
        meshRef.current.instanceMatrix.needsUpdate = true;
    });

    return (
        <instancedMesh ref={meshRef} args={[undefined, undefined, count]} frustumCulled={false}>
            <cylinderGeometry args={[0.02, 0.02, 0.55, 4]} />
            <meshBasicMaterial color={tint} transparent opacity={0.45} depthWrite={false} />
        </instancedMesh>
    );
}

function SnowField({ count = 300 }) {
    const meshRef = useRef();
    const dummy = useMemo(() => new THREE.Object3D(), []);
    const flakes = useMemo(() => {
        const arr = [];
        for (let i = 0; i < count; i++) {
            arr.push({
                x: (Math.random() - 0.5) * 80,
                y: 5 + Math.random() * 20,
                z: (Math.random() - 0.5) * 80,
                s: 0.6 + Math.random() * 1.5,
                sway: Math.random() * Math.PI * 2,
            });
        }
        return arr;
    }, [count]);

    useFrame((state, dt) => {
        if (!meshRef.current) return;
        const t = state.clock.getElapsedTime();
        for (let i = 0; i < flakes.length; i++) {
            const f = flakes[i];
            f.y -= f.s * dt;
            const sway = Math.sin(t + f.sway) * 0.35;
            if (f.y < 0) f.y = 25 + Math.random() * 5;
            dummy.position.set(f.x + sway, f.y, f.z + sway * 0.5);
            dummy.updateMatrix();
            meshRef.current.setMatrixAt(i, dummy.matrix);
        }
        meshRef.current.instanceMatrix.needsUpdate = true;
    });

    return (
        <instancedMesh ref={meshRef} args={[undefined, undefined, count]} frustumCulled={false}>
            <sphereGeometry args={[0.08, 4, 4]} />
            <meshBasicMaterial color="#ffffff" transparent opacity={0.85} depthWrite={false} />
        </instancedMesh>
    );
}

function StarField({ count = 220 }) {
    const positions = useMemo(() => {
        const arr = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
            // Upper hemisphere
            const u = Math.random();
            const v = Math.random() * 0.5;
            const r = 200;
            const theta = 2 * Math.PI * u;
            const phi = Math.acos(1 - 2 * v);
            arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
            arr[i * 3 + 1] = r * Math.abs(Math.cos(phi)) + 30;
            arr[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
        }
        return arr;
    }, [count]);
    return (
        <points>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={count}
                    array={positions}
                    itemSize={3}
                />
            </bufferGeometry>
            <pointsMaterial size={0.6} sizeAttenuation color="#e0e6ff" transparent opacity={0.85} />
        </points>
    );
}
